import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Decision, ShortlistEntry, Summary } from "@idea-studio/shared";
import { getRecentDecisions } from "../api/client";

interface Props {
  summary: Summary;
  onBack: () => void;
}

const SCORE_KEYS = ["P", "M", "T", "Mo", "D", "UA", "FF", "TP"] as const;

export function InsightsScreen({ summary, onBack }: Props) {
  const { current_focus: focus, shortlist, mode, pending_actions, last_updated_at } = summary;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="text-[14px] text-neutral-400 transition-colors active:text-neutral-200"
        >
          Terug
        </button>
        <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
          Inzichten
        </span>
        <span className="w-12" aria-hidden="true" />
      </div>

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
          <span>Cyclus {String(focus.active_cycle ?? "—")}</span>
          <span className="text-neutral-700">·</span>
          <span>{mode}</span>
          {focus.discovery_mode && focus.discovery_mode !== mode && (
            <>
              <span className="text-neutral-700">·</span>
              <span>{focus.discovery_mode}</span>
            </>
          )}
        </div>
        {last_updated_at && (
          <p className="font-mono text-[11px] text-neutral-600">
            laatst bijgewerkt {formatDate(last_updated_at)}
          </p>
        )}
      </header>

      <Section label="Volgende actie">
        <p className="text-[15px] leading-relaxed text-neutral-100">
          {focus.next_action ?? "—"}
        </p>
      </Section>

      {focus.blocker && (
        <Section label="Blokkade">
          <p className="text-[15px] leading-relaxed text-amber-200/90">{focus.blocker}</p>
        </Section>
      )}

      {focus.stop_condition && (
        <Section label="Stopconditie">
          <p className="text-[14px] text-neutral-300">{focus.stop_condition}</p>
        </Section>
      )}

      {pending_actions.length > 0 && (
        <Section label={`Pending acties · ${pending_actions.length}`}>
          <ul className="space-y-1.5 font-mono text-[12px] text-neutral-400">
            {pending_actions.map((a) => (
              <li key={a} className="break-all">
                {a}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <DecisionsStrip />

      <Section label={`Shortlist · ${shortlist.length}`}>
        <ul className="divide-y divide-neutral-900">
          {shortlist.map((idea) => (
            <ShortlistRow key={idea.id} idea={idea} />
          ))}
        </ul>
      </Section>

      <footer className="border-t border-neutral-900 pt-4 font-mono text-[11px] text-neutral-600">
        backend {summary.source.backend} · gefetched{" "}
        {new Date(summary.source.fetched_at).toLocaleTimeString("nl-NL", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </footer>
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
        {label}
      </h2>
      {children}
    </section>
  );
}

function ShortlistRow({ idea }: { idea: ShortlistEntry }) {
  const scoreEntries = SCORE_KEYS.map((k) => [k, idea.scores?.[k]] as const).filter(
    ([, v]) => v !== undefined,
  );

  return (
    <li className="py-3.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-neutral-500">{idea.id}</span>
        <StatusTag status={idea.status} />
        {idea.market_scan_passed && (
          <span className="rounded-sm border border-emerald-700/40 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-emerald-300/90">
            market scan
          </span>
        )}
      </div>
      <h3 className="mt-1.5 text-[14px] font-medium leading-snug text-neutral-100">
        {idea.title}
      </h3>
      {idea.thesis && (
        <p className="mt-1 text-[12.5px] leading-relaxed text-neutral-500">{idea.thesis}</p>
      )}
      {scoreEntries.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10.5px] text-neutral-500">
          {scoreEntries.map(([k, v]) => (
            <span key={k}>
              <span className="text-neutral-600">{k}</span>
              <span className="ml-1 text-neutral-300">{v}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function StatusTag({ status }: { status: string }) {
  const tone: Record<string, string> = {
    top_candidate: "text-emerald-400",
    conviction_candidate: "text-amber-300",
    high_potential: "text-sky-400",
    interesting: "text-neutral-400",
    archived: "text-neutral-600",
  };
  return (
    <span
      className={
        "text-[9px] font-medium uppercase tracking-wider " +
        (tone[status] ?? "text-neutral-500")
      }
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DecisionsStrip() {
  const query = useQuery({
    queryKey: ["decisions"],
    queryFn: () => getRecentDecisions(10),
    refetchInterval: 30_000,
    retry: 1,
  });

  const decisions = query.data ?? [];
  if (query.isLoading) return null;
  if (decisions.length === 0) return null;

  const counts = decisions.reduce<Record<string, number>>((acc, d) => {
    acc[d.action] = (acc[d.action] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Section label={`Recente beslissingen · ${decisions.length}`}>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 font-mono text-[10.5px]">
        {Object.entries(counts).map(([action, n]) => (
          <span key={action}>
            <span className="text-neutral-600">{action.replace(/_/g, " ")}</span>
            <span className="ml-1 text-neutral-300">{n}</span>
          </span>
        ))}
      </div>
      <ul className="mt-3 divide-y divide-neutral-900/80 border-t border-neutral-900/80">
        {decisions.slice(0, 5).map((d) => (
          <DecisionRow key={`${d.idea_id}-${d.decided_at}`} decision={d} />
        ))}
      </ul>
    </Section>
  );
}

function DecisionRow({ decision }: { decision: Decision }) {
  return (
    <li className="py-2.5">
      <div className="flex items-center gap-2 font-mono text-[10.5px] text-neutral-500">
        <span className="text-neutral-400">{decision.idea_id}</span>
        <span className="text-neutral-700">·</span>
        <span className="uppercase tracking-wider">
          {decision.action.replace(/_/g, " ")}
        </span>
        <span className="text-neutral-700">·</span>
        <span>{formatDate(decision.decided_at)}</span>
      </div>
      {decision.title && (
        <p className="mt-1 truncate text-[12.5px] text-neutral-300">
          {decision.title}
        </p>
      )}
      {decision.reason && (
        <p className="mt-0.5 truncate text-[11.5px] text-neutral-500">
          {decision.reason}
        </p>
      )}
    </li>
  );
}
